<div class="flex">
  <main [ngClass.lt-md]="{ mobile: true }" class="subnav__exists w-full flex-1 flex flex-col gap-6">
    @if (!loading && domain) {
      <sbnb-content-header
        [title]="domain.name"
        backLink="/settings/domains"
        novaContentId="domains-edit"
        [saveButtonShown]="false"></sbnb-content-header>

      <!-- Add the nameserver setup component here -->
      @if (domain.dnsManaged && !domain.nameserverVerifiedAt) {
        <sbnb-unlinked-managed-domain-meta
          [domainUuid]="domainUuid"
          [domainName]="domain.name"
          (domainVerified)="handleDomainVerified()"
          class="mb-6"></sbnb-unlinked-managed-domain-meta>
      }

      <!-- Domain Information Card -->
      <div class="mat-elevation-z1 p-6 rounded">
        <h3 class="text-lg font-medium mb-2">Domain Information</h3>

        <div class="grid grid-cols-1 sbnbDesktop:grid-cols-2 gap-2">
          <div>
            <p class="text-sm text-night-lighter">Domain Name</p>
            <p class="font-medium">{{ domain.name }}</p>
          </div>
          <div>
            <p class="text-sm text-night-lighter">Managed By</p>
            <p class="font-medium">{{ domain.dnsManaged ? 'Hospitable' : 'External' }}</p>
          </div>
          <div>
            <p class="text-sm text-night-lighter">Created On</p>
            <p class="font-medium">{{ domain.createdAt | date }}</p>
          </div>
          @if (domain.expiresAt) {
            <div>
              <p class="text-sm text-night-lighter">Expires On</p>
              <p class="font-medium">{{ domain.expiresAt | date }}</p>
            </div>
          }
          @if (domain.siteId) {
            <div>
              <p class="text-sm text-night-lighter">Linked to Site</p>
              <p class="font-medium">
                <sbnb-button
                  type="secondary"
                  size="mini"
                  label="View Site Details"
                  [routerLink]="['/direct/sites', domain.siteId]"></sbnb-button>
              </p>
            </div>
          }
        </div>
      </div>

      <!-- DNS Records Card -->
      <div class="mat-elevation-z1 bg-white p-6 rounded">
        <div class="flex justify-between items-center mb-4">
          <h3>DNS Records</h3>

          @if (!isAddingNewRecord && domain?.dnsManaged) {
            <sbnb-button
              size="mini"
              type="secondary"
              [label]="'+ Add Record'"
              (clicked)="showAddRecord()"></sbnb-button>
          }
        </div>

        @if (!domain.dnsManaged) {
          <sbnb-externally-managed-dns />
        }

        @if (domain.dnsManaged) {
          <!-- Top form for adding new records -->
          @if (isAddingNewRecord) {
            <div class="rounded-md p-4 mb-4">
              <h4 class="mb-4">Add DNS Record</h4>
              <form [formGroup]="recordForm">
                <div class="grid grid-cols-1 sbnbDesktop:grid-cols-2 gap-4">
                  <div>
                    <label>Type</label>
                    <mat-form-field class="w-full">
                      <mat-select formControlName="type">
                        @for (type of recordTypes; track type) {
                          <mat-option [value]="type">{{ type }}</mat-option>
                        }
                      </mat-select>
                    </mat-form-field>
                  </div>
                  <div>
                    <label>Name</label>
                    <mat-form-field class="w-full">
                      <input matInput formControlName="name" type="text" placeholder="e.g. @ or www" />
                    </mat-form-field>
                  </div>
                  <div>
                    <label>TTL</label>
                    <mat-form-field class="w-full">
                      <mat-select formControlName="ttl">
                        @for (option of ttlOptions; track option) {
                          <mat-option [value]="option.value">{{ option.label }}</mat-option>
                        }
                      </mat-select>
                    </mat-form-field>
                  </div>
                  <div>
                    <label>Value</label>
                    <mat-form-field class="w-full">
                      <input matInput formControlName="content" type="text" placeholder="e.g. 192.168.1.1" />
                    </mat-form-field>
                  </div>
                  @if (recordForm.value.type === 'MX') {
                    <div>
                      <label>Priority</label>
                      <mat-form-field class="w-full">
                        <input matInput formControlName="priority" type="number" />
                      </mat-form-field>
                    </div>
                  }
                </div>
                <div class="mt-4 flex justify-end space-x-2">
                  <sbnb-button size="mini" type="secondary" [label]="'Cancel'" (clicked)="cancelEdit()"></sbnb-button>
                  <sbnb-button
                    size="mini"
                    type="primary"
                    [label]="savingRecord ? 'Saving...' : 'Save'"
                    [loading]="savingRecord"
                    [disabled]="recordForm.invalid || savingRecord"
                    (clicked)="saveRecord()"></sbnb-button>
                </div>
              </form>
            </div>
          }

          <!-- DNS Records Table -->
          @if (domain?.dnsRecords?.length === 0) {
            <div class="p-4 text-center text-night-lighter">
              <span>No DNS records found</span>
            </div>
          }

          @if (domain?.dnsRecords?.length > 0) {
            <table class="min-w-full">
              <thead>
                <tr>
                  <th class="px-3 py-2 text-left text-xs font-medium text-night-lighter tracking-wider">Type</th>
                  <th class="px-3 py-2 text-left text-xs font-medium text-night-lighter tracking-wider">Name</th>
                  <th class="px-3 py-2 text-left text-xs font-medium text-night-lighter tracking-wider">TTL</th>
                  <th class="px-3 py-2 text-left text-xs font-medium text-night-lighter tracking-wider">Value</th>
                  <th class="px-3 py-2 text-left text-xs font-medium text-night-lighter tracking-wider">Status</th>
                  <th class="px-3 py-2 w-10"></th>
                </tr>
              </thead>
              <tbody>
                @for (record of domain.dnsRecords; track record) {
                  <!-- Record row -->
                  <tr class="border-b border-smoke">
                    <td class="px-3 py-2 whitespace-nowrap text-sm">{{ record.type }}</td>
                    <td class="px-3 py-2 whitespace-nowrap text-sm">{{ record.name }}</td>
                    <td class="px-3 py-2 whitespace-nowrap text-sm">{{ getTtlLabel(record.ttl) }}</td>
                    <td class="px-3 py-2 whitespace-nowrap text-sm overflow-hidden text-ellipsis">
                      <div class="max-w-md truncate" [matTooltip]="record.content">{{ record.content }}</div>
                    </td>
                    <td class="px-3 py-2 whitespace-nowrap text-sm">
                      @if (record.record_locked) {
                        <span
                          class="badge badge-lemon"
                          matTooltip="This DNS record connects your domain to your Direct site. If you want to remove this record, you need to disconnect the domain from your site."
                          matTooltipPosition="above">
                          Locked
                        </span>
                      }
                    </td>

                    <!-- Actions column -->
                    @if (!record.record_locked) {
                      <td class="px-3 py-2 whitespace-nowrap text-sm text-right">
                        <button
                          [matMenuTriggerFor]="recordMenu"
                          class="text-night-lighter hover:text-blue-600 focus:outline-none">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            class="h-5 w-5"
                            viewBox="0 0 20 20"
                            fill="currentColor">
                            <path
                              d="M6 10a2 2 0 11-4 0 2 2 0 014 0zM12 10a2 2 0 11-4 0 2 2 0 014 0zM18 10a2 2 0 11-4 0 2 2 0 014 0z" />
                          </svg>
                        </button>

                        <mat-menu #recordMenu="matMenu" class="w-48">
                          <button mat-menu-item (click)="editRecord(record)">Edit</button>
                          <button mat-menu-item (click)="deleteRecord(record)" class="text-red-600 hover:bg-red-50">
                            Delete
                          </button>
                        </mat-menu>
                      </td>
                    } @else {
                      <td class="px-3 py-2 whitespace-nowrap text-sm text-right">
                        <!-- Empty cell for locked records -->
                      </td>
                    }
                  </tr>

                  <!-- Edit form row - shown when editing this record -->
                  @if (editingRecordId === record.id) {
                    <tr class="border-b border-smoke">
                      <td colspan="6" class="px-4 py-3">
                        <form [formGroup]="recordForm">
                          <div class="grid grid-cols-1 sbnbDesktop:grid-cols-2 gap-4">
                            <div>
                              <label>Type</label>
                              <mat-form-field class="w-full">
                                <mat-select formControlName="type">
                                  @for (type of recordTypes; track type) {
                                    <mat-option [value]="type">{{ type }}</mat-option>
                                  }
                                </mat-select>
                              </mat-form-field>
                            </div>
                            <div>
                              <label>Name</label>
                              <mat-form-field class="w-full">
                                <input matInput formControlName="name" type="text" placeholder="e.g. @ or www" />
                              </mat-form-field>
                            </div>
                            <div>
                              <label>TTL</label>
                              <mat-form-field class="w-full">
                                <mat-select formControlName="ttl">
                                  @for (option of ttlOptions; track option) {
                                    <mat-option [value]="option.value">{{ option.label }}</mat-option>
                                  }
                                </mat-select>
                              </mat-form-field>
                            </div>
                            <div>
                              <label>Value</label>
                              <mat-form-field class="w-full">
                                <input matInput formControlName="content" type="text" placeholder="e.g. 192.168.1.1" />
                              </mat-form-field>
                            </div>
                            @if (recordForm.value.type === 'MX') {
                              <div>
                                <label>Priority</label>
                                <mat-form-field class="w-full">
                                  <input matInput formControlName="priority" type="number" />
                                </mat-form-field>
                              </div>
                            }
                          </div>
                          <div class="mt-4 flex justify-end space-x-2">
                            <sbnb-button
                              size="mini"
                              type="secondary"
                              [label]="'Cancel'"
                              (clicked)="cancelEdit()"></sbnb-button>
                            <sbnb-button
                              size="mini"
                              type="primary"
                              [label]="savingRecord ? 'Updating...' : 'Update'"
                              [loading]="savingRecord"
                              [disabled]="recordForm.invalid || savingRecord"
                              (clicked)="saveRecord()"></sbnb-button>
                          </div>
                        </form>
                      </td>
                    </tr>
                  }
                }
              </tbody>
            </table>
          }
        }
      </div>

      <!-- Delete Domain Card -->
      <div class="mat-elevation-z1 bg-white p-6 rounded">
        <h3 class="text-lg font-medium mb-2">Delete Domain</h3>
        <p class="mb-4">This action cannot be undone. Deleting your domain will remove it permanently.</p>
        <sbnb-button size="mini" type="secondary" [label]="'Delete Domain'" (clicked)="deleteDomain()"></sbnb-button>
      </div>
    } @else {
      <div class="w-full flex items-center justify-center h-64">
        <mat-spinner diameter="50"></mat-spinner>
      </div>
    }

    @if (loading) {
      <div class="space-y-6">
        <!-- Skeleton for header -->
        <div class="h-8 w-64 rounded animate-pulse"></div>

        <!-- Skeleton for domain information card -->
        <div class="bg-white p-6 rounded shadow">
          <div class="h-6 w-48 rounded animate-pulse mb-4"></div>
          <div class="grid grid-cols-1 sbnbDesktop:grid-cols-2 gap-4">
            @for (i of [1, 2, 3, 4]; track i) {
              <div>
                <div class="h-4 w-24 rounded animate-pulse mb-2"></div>
                <div class="h-5 w-32 rounded animate-pulse"></div>
              </div>
            }
          </div>
        </div>

        <!-- Skeleton for DNS records card -->
        <div class="bg-white p-6 rounded shadow">
          <div class="flex justify-between items-center mb-4">
            <div class="h-6 w-32 rounded animate-pulse"></div>
            <div class="h-8 w-24 rounded animate-pulse"></div>
          </div>

          <!-- Skeleton for DNS records table -->
          <div class="space-y-4">
            <div class="h-10 rounded animate-pulse"></div>
            @for (i of [1, 2, 3]; track i) {
              <div class="h-12 rounded animate-pulse"></div>
            }
          </div>
        </div>
      </div>
    }
  </main>
</div>
